import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getMilestonePathTimeline } from '../FlowchartHUD/utils/milestoneHudProgress';
import { getStatusConfig } from '../../utils/statusConfig';
import './MilestonePathModal.css';

function getStepStatusLabel(step, statuses) {
  const { task, nodeType, complete } = step;
  if (nodeType === 'decision') {
    if (task.answer != null) return 'Answered';
    return 'Needs answer';
  }
  const config = getStatusConfig(task.status, statuses);
  return config?.name || config?.label || 'Unknown';
}

function getStepStatusColor(step, statuses) {
  const { task, nodeType } = step;
  if (nodeType === 'decision') {
    return task.answer != null ? '#86efac' : '#fbbf24';
  }
  return getStatusConfig(task.status, statuses)?.color || '#94a3b8';
}

const MilestonePathModal = ({
  milestone,
  tasks = [],
  statuses = [],
  nestedSprints = [],
  allSprints = [],
  isOpen,
  onClose,
  onGoToTask,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const timeline = useMemo(() => {
    if (!milestone) return null;
    return getMilestonePathTimeline(
      milestone,
      tasks,
      statuses,
      nestedSprints,
      allSprints,
    );
  }, [milestone, tasks, statuses, nestedSprints, allSprints]);

  if (!isOpen || !milestone || !timeline) return null;

  const {
    reached, progressPercent, baselineMilestone, steps,
  } = timeline;
  const milestoneName = milestone.name || 'Untitled milestone';
  const firstIncompleteIdx = steps.findIndex((s) => !s.complete);
  const completedCount = steps.filter((s) => s.complete).length;
  const totalCount = steps.length;

  const subtitle = reached
    ? 'This milestone has been reached.'
    : totalCount > 0
      ? `${completedCount} of ${totalCount} on the critical path complete.`
      : 'Unlocks directly from its host mission gate.';

  return createPortal(
    <>
      <div className="milestone-path-backdrop" onClick={onClose} role="presentation" />
      <div
        className={[
          'milestone-path-modal',
          reached && 'milestone-path-modal--reached',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-labelledby="milestone-path-title"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="milestone-path-modal__header">
          <span className="milestone-path-modal__badge" aria-hidden="true">🚩</span>
          <div className="milestone-path-modal__title-block">
            <h2 id="milestone-path-title" className="milestone-path-modal__title">
              {milestoneName}
            </h2>
            <p className="milestone-path-modal__subtitle">{subtitle}</p>
          </div>
          <span
            className={[
              'milestone-path-modal__percent',
              reached && 'milestone-path-modal__percent--reached',
            ].filter(Boolean).join(' ')}
          >
            {progressPercent}%
          </span>
          <button type="button" className="milestone-path-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="milestone-path-modal__progress" aria-hidden="true">
          <div
            className={[
              'milestone-path-modal__progress-fill',
              reached && 'milestone-path-modal__progress-fill--reached',
            ].filter(Boolean).join(' ')}
            style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
          />
        </div>

        <div className="milestone-path-modal__body">
          {baselineMilestone && (
            <div className="milestone-path-modal__baseline">
              <span aria-hidden="true">🏁</span>
              Progress starts after &ldquo;{baselineMilestone.name || 'Untitled'}&rdquo; was reached.
            </div>
          )}

          {steps.length === 0 ? (
            <div className="milestone-path-modal__empty">
              No missions on the critical path — this milestone unlocks from its host mission gate.
            </div>
          ) : (
            <ol className="milestone-path-timeline">
              {steps.map((step, index) => {
                const isCurrent = !reached && index === firstIncompleteIdx;
                const statusLabel = getStepStatusLabel(step, statuses);
                const statusColor = getStepStatusColor(step, statuses);
                const typeLabel = step.nodeType === 'decision' ? 'Decision' : 'Mission';

                return (
                  <li key={step.task.id} className="milestone-path-timeline__item">
                    <div className="milestone-path-timeline__rail" aria-hidden="true">
                      <span
                        className={[
                          'milestone-path-timeline__dot',
                          step.complete && 'milestone-path-timeline__dot--complete',
                          isCurrent && 'milestone-path-timeline__dot--current',
                        ].filter(Boolean).join(' ')}
                      />
                      <span
                        className={[
                          'milestone-path-timeline__line',
                          step.complete && 'milestone-path-timeline__line--complete',
                        ].filter(Boolean).join(' ')}
                      />
                    </div>
                    <div className="milestone-path-timeline__content">
                      <button
                        type="button"
                        className={[
                          'milestone-path-timeline__step-btn',
                          step.complete && 'milestone-path-timeline__step-btn--complete',
                        ].filter(Boolean).join(' ')}
                        onClick={() => onGoToTask?.(step.task.id)}
                      >
                        <span className="milestone-path-timeline__name">
                          {step.task.name || 'Untitled'}
                        </span>
                        <span className="milestone-path-timeline__meta">
                          <span className="milestone-path-timeline__status">
                            <span
                              className="milestone-path-timeline__status-dot"
                              style={{ background: statusColor }}
                            />
                            {statusLabel}
                          </span>
                          <span className="milestone-path-timeline__type">{typeLabel}</span>
                        </span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="milestone-path-timeline__goal">
            <div className="milestone-path-timeline__rail" aria-hidden="true">
              <span className="milestone-path-timeline__dot" />
            </div>
            <div className="milestone-path-timeline__content">
              <span
                className={[
                  'milestone-path-timeline__goal-label',
                  reached && 'milestone-path-timeline__goal-label--reached',
                ].filter(Boolean).join(' ')}
              >
                {reached ? '✓ Milestone reached' : '🚩 Milestone unlocks here'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MilestonePathModal;
